// components/tour/TourContext.jsx
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import generalApi from '../../generalApi';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourConfig, setTourConfig] = useState(null);
  const [isWelcomeVisible, setIsWelcomeVisible] = useState(false);
  const [isCompleteVisible, setIsCompleteVisible] = useState(false);

  const startTour = useCallback((config) => {
    setTourConfig(config);
    setCurrentStep(0);
    setIsWelcomeVisible(true);
    setIsActive(true);
  }, []);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    setTourConfig(null);
    setIsWelcomeVisible(false);
    setIsCompleteVisible(false);
  }, []);

  const nextStep = useCallback(() => {
    if (tourConfig && currentStep < tourConfig.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Tour completed
      setIsCompleteVisible(true);
    }
  }, [currentStep, tourConfig]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const skipToStep = useCallback((stepIndex) => {
    if (tourConfig && stepIndex >= 0 && stepIndex < tourConfig.steps.length) {
      setCurrentStep(stepIndex);
    }
  }, [tourConfig]);

  const completeTour = useCallback(async () => {
    try {
      await generalApi.post('/auth/complete-tour');
      endTour();
    } catch (error) {
      console.error('Error completing tour:', error);
      // Still end the tour even if API call fails
      endTour();
    }
  }, [endTour]);

  const skipTour = useCallback(async () => {
    try {
      await generalApi.post('/auth/skip-tour');
      endTour();
    } catch (error) {
      console.error('Error skipping tour:', error);
      // Still end the tour even if API call fails
      endTour();
    }
  }, [endTour]);

  const startWelcome = useCallback(() => {
    setIsWelcomeVisible(false);
    // Small delay to allow welcome to close before starting tour
    setTimeout(() => {
      if (tourConfig && tourConfig.steps.length > 0) {
        setCurrentStep(0);
      }
    }, 300);
  }, [tourConfig]);

  const getCurrentStepConfig = useCallback(() => {
    if (!tourConfig || !tourConfig.steps || currentStep >= tourConfig.steps.length) {
      return null;
    }
    return tourConfig.steps[currentStep];
  }, [tourConfig, currentStep]);

  const getTotalSteps = useCallback(() => {
    return tourConfig?.steps?.length || 0;
  }, [tourConfig]);

  // FIXED: Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // State
    isActive,
    currentStep,
    tourConfig,
    isWelcomeVisible,
    isCompleteVisible,

    // Actions
    startTour,
    endTour,
    nextStep,
    previousStep,
    skipToStep,
    completeTour,
    skipTour,
    startWelcome,

    // Helpers
    getCurrentStepConfig,
    getTotalSteps,

    // Internal state setters (for components)
    setIsWelcomeVisible,
    setIsCompleteVisible
  }), [
    isActive,
    currentStep,
    tourConfig,
    isWelcomeVisible,
    isCompleteVisible,
    startTour,
    endTour,
    nextStep,
    previousStep,
    skipToStep,
    completeTour,
    skipTour,
    startWelcome,
    getCurrentStepConfig,
    getTotalSteps,
    setIsWelcomeVisible,
    setIsCompleteVisible
  ]);

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};